import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Receipt, Camera, Upload, Trash2, Pencil, Check, X as XIcon,
  Download, Calendar, ChevronDown, ChevronUp, Loader2, AlertCircle,
  CheckCircle2, ImageIcon, FileText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { invoicesAPI, projectsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import PhotoLightbox from '../components/PhotoLightbox';

const fmtBR = (iso) => {
  if (!iso) return '—';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};
const fmtCurrency = (v) => {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
};

// ===== EDITABLE ROW =====
const CATEGORY_COLORS = {
  COMBUSTIVEL: 'bg-blue-100 text-blue-700',
  ALIMENTACAO: 'bg-orange-100 text-orange-700',
  HOTEL: 'bg-purple-100 text-purple-700',
  PEDAGIO: 'bg-cyan-100 text-cyan-700',
  TRANSPORTE: 'bg-emerald-100 text-emerald-700',
  MATERIAL: 'bg-amber-100 text-amber-700',
  OUTROS: 'bg-gray-100 text-gray-700',
};

const InvoiceRow = ({ inv, categories, canEditAll, token, onChange, onDelete, onOpenPhoto }) => {
  const [editing, setEditing] = useState(false);
  const [draftDate, setDraftDate] = useState(inv.purchase_date || '');
  const [draftItem, setDraftItem] = useState(inv.items_summary || '');
  const [draftValue, setDraftValue] = useState(inv.total_value ?? '');
  const [draftCategory, setDraftCategory] = useState(inv.category || 'OUTROS');
  const [saving, setSaving] = useState(false);

  const cancel = () => {
    setEditing(false);
    setDraftDate(inv.purchase_date || '');
    setDraftItem(inv.items_summary || '');
    setDraftValue(inv.total_value ?? '');
    setDraftCategory(inv.category || 'OUTROS');
  };
  const save = async () => {
    setSaving(true);
    try {
      const v = String(draftValue).replace(',', '.');
      const num = parseFloat(v);
      const res = await invoicesAPI.patch(inv.id, {
        purchase_date: draftDate || null,
        items_summary: draftItem,
        total_value: isNaN(num) ? null : num,
        category: draftCategory,
        status: 'reviewed',
      });
      onChange(res.data);
      setEditing(false);
      toast.success('Nota fiscal atualizada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir esta nota fiscal?')) return;
    try {
      await invoicesAPI.delete(inv.id);
      onDelete(inv.id);
      toast.success('Nota excluída');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-2 align-middle">
        {inv.thumbnail_url ? (
          <button
            type="button"
            onClick={() => onOpenPhoto(inv)}
            className="w-12 h-12 rounded overflow-hidden border border-gray-200 hover:border-gold transition-colors"
            data-testid={`invoice-thumb-${inv.id}`}
          >
            <img
              src={`/api/invoices/${inv.id}/photo?auth=${encodeURIComponent(token)}`}
              alt="nota"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ) : <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center"><ImageIcon size={14} className="text-gray-400" /></div>}
      </td>
      <td className="px-2 py-2 align-middle">
        {editing ? (
          <Input
            type="date"
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
            className="h-8 text-xs w-36"
            data-testid={`invoice-date-input-${inv.id}`}
          />
        ) : (
          <span className="text-navy text-sm font-mono">{fmtBR(inv.purchase_date)}</span>
        )}
      </td>
      <td className="px-2 py-2 align-middle">
        {editing ? (
          <Input
            value={draftItem}
            onChange={(e) => setDraftItem(e.target.value)}
            placeholder="Descrição do item"
            className="h-8 text-xs w-full"
            data-testid={`invoice-item-input-${inv.id}`}
          />
        ) : (
          <div>
            <p className="text-navy text-sm" data-testid={`invoice-item-${inv.id}`}>{inv.items_summary || '—'}</p>
            {inv.user_name && (
              <p className="text-gray-400 text-[10px] font-mono mt-0.5">por {inv.user_name}</p>
            )}
          </div>
        )}
      </td>
      <td className="px-2 py-2 align-middle text-right">
        {editing ? (
          <Input
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            placeholder="0,00"
            className="h-8 text-xs w-28 text-right font-mono"
            data-testid={`invoice-value-input-${inv.id}`}
          />
        ) : (
          <span className="text-navy text-sm font-semibold font-mono" data-testid={`invoice-value-${inv.id}`}>
            {fmtCurrency(inv.total_value)}
          </span>
        )}
      </td>
      <td className="px-2 py-2 align-middle">
        {editing ? (
          <select
            value={draftCategory}
            onChange={(e) => setDraftCategory(e.target.value)}
            className="h-8 text-xs border border-gray-200 rounded px-1 bg-white w-full"
            data-testid={`invoice-category-input-${inv.id}`}
          >
            {(categories || []).map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        ) : (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${CATEGORY_COLORS[inv.category] || CATEGORY_COLORS.OUTROS}`}
            data-testid={`invoice-category-${inv.id}`}
          >
            {(categories?.find((c) => c.code === inv.category)?.label) || inv.category || 'Outros'}
          </span>
        )}
      </td>
      <td className="px-2 py-2 align-middle">
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
            inv.status === 'reviewed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}
          data-testid={`invoice-status-${inv.id}`}
        >
          {inv.status === 'reviewed' ? <><CheckCircle2 size={9}/> REVISADA</> : <><AlertCircle size={9}/> RASCUNHO</>}
        </span>
      </td>
      <td className="px-2 py-2 align-middle text-right whitespace-nowrap">
        {editing ? (
          <>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="h-7 px-2 text-[10px] btn-primary"
              data-testid={`invoice-save-${inv.id}`}
            >
              {saving ? <Loader2 size={11} className="animate-spin"/> : <Check size={11} />}
            </Button>
            <Button size="sm" variant="outline" onClick={cancel} className="h-7 px-2 text-[10px] ml-1" data-testid={`invoice-cancel-${inv.id}`}>
              <XIcon size={11} />
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-7 px-2 text-[10px]" data-testid={`invoice-edit-${inv.id}`}>
              <Pencil size={11} />
            </Button>
            <Button size="sm" variant="outline" onClick={handleDelete} className="h-7 px-2 text-[10px] ml-1 border-red-300 text-red-600 hover:bg-red-50" data-testid={`invoice-delete-${inv.id}`}>
              <Trash2 size={11} />
            </Button>
          </>
        )}
      </td>
    </tr>
  );
};

// ===== MAIN PAGE =====
const InvoicesPage = () => {
  const { user, isSuperAdmin, isAdmin } = useAuth();
  const token = localStorage.getItem('rpas_token');

  // Project selection
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Categories
  const [categories, setCategories] = useState([]);

  // Data
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadDone, setUploadDone] = useState(0);

  // Period filter
  const [period, setPeriod] = useState('month'); // week, month, year, custom
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // UI
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Fetch projects user has access to (with invoices enabled)
  useEffect(() => {
    const load = async () => {
      setLoadingProjects(true);
      try {
        const [projRes, catRes] = await Promise.all([
          projectsAPI.list(),
          invoicesAPI.categories().catch(() => ({ data: [] })),
        ]);
        const enabledProjects = (projRes.data || []).filter(p => p.invoices_enabled);
        setProjects(enabledProjects);
        setCategories(catRes.data || []);
        if (enabledProjects.length > 0 && !selectedProject) {
          // Prefer the user's primary project
          const primary = enabledProjects.find(p => p.id === user?.project_id) || enabledProjects[0];
          setSelectedProject(primary.id);
        }
      } catch (e) {
        toast.error('Falha ao carregar projetos');
      } finally {
        setLoadingProjects(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve period -> {start, end}
  const dateRange = useMemo(() => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (period === 'week') {
      const day = today.getDay() === 0 ? 6 : today.getDay() - 1;
      const s = new Date(today); s.setDate(today.getDate() - day);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return { start: fmt(s), end: fmt(e), label: `Esta semana (${fmt(s).slice(8)}/${fmt(s).slice(5,7)} - ${fmt(e).slice(8)}/${fmt(e).slice(5,7)})` };
    }
    if (period === 'month') {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: fmt(s), end: fmt(e), label: `Este mês` };
    }
    if (period === 'year') {
      return { start: `${today.getFullYear()}-01-01`, end: `${today.getFullYear()}-12-31`, label: `${today.getFullYear()}` };
    }
    return { start: customStart, end: customEnd, label: 'Período customizado' };
  }, [period, customStart, customEnd]);

  // Fetch invoices for selected project + period
  useEffect(() => {
    if (!selectedProject) return;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (dateRange.start) params.start = dateRange.start;
        if (dateRange.end) params.end = dateRange.end;
        const res = await invoicesAPI.list(selectedProject, params);
        setInvoices(res.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Falha ao carregar notas');
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedProject, dateRange.start, dateRange.end]);

  // Client-side image preprocessing — runs in the browser before upload.
  // Downsizes to max 1600px and re-encodes to JPEG q=85 so we send ~70% less bytes,
  // which makes the request to the backend faster AND helps with mobile data plans.
  // Returns the original File if anything goes wrong (defensive).
  const downscaleImageClientSide = async (file) => {
    try {
      // Skip preprocessing for HEIC/HEIF on the client (browsers can't decode them);
      // backend will handle these with pillow_heif.
      const isHeic = /\.heic|\.heif$/i.test(file.name) || /heic|heif/i.test(file.type);
      if (isHeic) return file;
      // Tiny files (already small) — skip
      if (file.size <= 600_000) return file;

      const bitmap = await createImageBitmap(file).catch(() => null);
      if (!bitmap) return file;
      const MAX = 1600;
      const ratio = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
      if (ratio === 1) {
        bitmap.close?.();
        return file;
      }
      const w = Math.round(bitmap.width * ratio);
      const h = Math.round(bitmap.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close?.();
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
      if (!blob || blob.size === 0) return file;
      // Only use the downsized version if it really saved space
      if (blob.size >= file.size) return file;
      return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    } catch (e) {
      console.warn('Client downscale failed:', e);
      return file;
    }
  };

  // Poll a freshly uploaded invoice until OCR finishes (or fails / times out)
  const pollUntilOcrDone = async (invoiceId, fileName) => {
    const MAX_ATTEMPTS = 30; // ~60s total
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const res = await invoicesAPI.get(invoiceId);
        const ocrStatus = res.data.ocr_status || 'processing';
        if (ocrStatus !== 'processing') {
          return res.data;
        }
      } catch (e) {
        // Network blip — keep retrying
      }
    }
    return null; // gave up; UI will keep showing "Processando..."
  };

  const handleUpload = async (filesList) => {
    if (!filesList || filesList.length === 0) return;
    if (!selectedProject) {
      toast.error('Selecione um projeto antes');
      return;
    }
    const files = Array.from(filesList);
    setUploadCount(files.length);
    setUploadDone(0);
    let added = [];
    let abortedDueToBudget = false;
    for (const f of files) {
      if (abortedDueToBudget) {
        toast.dismiss(f.name);
        continue;
      }
      try {
        toast.loading(`Enviando ${f.name}...`, { id: f.name });
        const optimized = await downscaleImageClientSide(f);
        const res = await invoicesAPI.upload(selectedProject, optimized);
        const created = res.data;
        added.push(created);
        // Add placeholder card immediately so user sees the upload landed
        setInvoices((prev) => [created, ...prev]);

        // Poll until OCR finishes (background)
        toast.loading(`Lendo nota: ${f.name}...`, { id: f.name });
        const finalDoc = await pollUntilOcrDone(created.id, f.name);
        if (finalDoc) {
          // Update the placeholder with the real OCR results
          setInvoices((prev) => prev.map((x) => (x.id === finalDoc.id ? finalDoc : x)));
          if (finalDoc.ocr_status === 'done') {
            toast.success(`OCR ok: ${finalDoc.items_summary || 'nota processada'}`, { id: f.name });
          } else if (finalDoc.ocr_status === 'timeout') {
            toast(`OCR demorou. Edite os campos manualmente.`, { id: f.name, icon: '⏱️', duration: 8000 });
          } else if (finalDoc.ocr_status === 'failed') {
            toast.error(`OCR falhou: ${finalDoc.ocr_error || 'edite manualmente'}`, { id: f.name, duration: 8000 });
          }
        } else {
          toast(`Upload ok. OCR ainda processando — verifique em segundos.`, { id: f.name, icon: '⏳', duration: 6000 });
        }
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail || e.message;
        if (status === 402) {
          abortedDueToBudget = true;
          toast.error(detail, { id: f.name, duration: 15000 });
        } else {
          toast.error(`Erro em ${f.name}: ${detail}`, { id: f.name });
        }
      } finally {
        setUploadDone((d) => d + 1);
      }
    }
    setUploadCount(0);
    setUploadDone(0);
  };

  const total = invoices.reduce((s, it) => s + (it.total_value || 0), 0);
  const canExport = isSuperAdmin || isAdmin;

  // Totals by category (for the summary cards shown above the table)
  const totalsByCategory = useMemo(() => {
    const acc = {};
    for (const it of invoices) {
      const cat = it.category || 'OUTROS';
      acc[cat] = acc[cat] || { count: 0, total: 0 };
      acc[cat].count += 1;
      acc[cat].total += it.total_value || 0;
    }
    // Sort by total desc
    return Object.entries(acc)
      .map(([code, agg]) => ({ code, label: categories.find((c) => c.code === code)?.label || code, ...agg }))
      .sort((a, b) => b.total - a.total);
  }, [invoices, categories]);

  const handleExport = () => {
    if (!selectedProject) return;
    if (period === 'custom' && (!customStart || !customEnd)) {
      toast.error('Selecione as datas de início e fim');
      return;
    }
    const url = invoicesAPI.exportUrl(selectedProject, period, dateRange.start, dateRange.end, token);
    window.open(url, '_blank');
  };

  // Lightbox photos (use raw invoice photo URLs)
  const lightboxItems = invoices
    .filter((iv) => iv.thumbnail_url)
    .map((iv) => ({
      id: iv.id,
      src: `/api/invoices/${iv.id}/photo?auth=${encodeURIComponent(token)}`,
      fullSrc: `/api/invoices/${iv.id}/photo?auth=${encodeURIComponent(token)}`,
      original_filename: iv.items_summary || 'Nota fiscal',
    }));

  const openPhotoFor = (inv) => {
    const idx = lightboxItems.findIndex((x) => x.id === inv.id);
    if (idx >= 0) setLightboxIndex(idx);
  };

  // ===== RENDER =====

  if (loadingProjects) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 size={28} className="text-gold animate-spin" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="invoices-no-access">
        <div className="card-hud p-8 text-center">
          <Receipt size={36} className="text-gray-300 mx-auto mb-2" />
          <h2 className="text-navy font-rajdhani font-bold text-lg uppercase tracking-wider mb-1">
            PRESTAÇÃO DE CONTAS
          </h2>
          <p className="text-gray-500 text-sm mt-2">
            Nenhum projeto disponível.<br />
            {isSuperAdmin
              ? 'Habilite a funcionalidade em PROJETOS para começar a usar.'
              : 'Solicite ao seu administrador que habilite a funcionalidade.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4" data-testid="invoices-page">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Receipt size={22} className="text-gold" />
          <div>
            <h1 className="text-navy font-rajdhani font-bold text-lg uppercase tracking-wider">
              PRESTAÇÃO DE CONTAS
            </h1>
            <p className="text-gray-500 text-[11px] font-mono">
              {invoices.length} {invoices.length === 1 ? 'nota' : 'notas'} · Total {fmtCurrency(total)}
            </p>
          </div>
        </div>

        {projects.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Projeto:</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="border border-gray-200 rounded px-2 py-1 text-xs bg-white"
              data-testid="invoices-project-select"
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* PERIOD FILTER + EXPORT */}
      <div className="card-hud p-3 flex items-center gap-2 flex-wrap">
        <Calendar size={14} className="text-gold" />
        <span className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Período:</span>
        {[
          { v: 'week', l: 'SEMANA' },
          { v: 'month', l: 'MÊS' },
          { v: 'year', l: 'ANO' },
          { v: 'custom', l: 'CUSTOMIZADO' },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setPeriod(opt.v)}
            className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
              period === opt.v
                ? 'bg-gold text-navy'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            data-testid={`invoices-period-${opt.v}`}
          >
            {opt.l}
          </button>
        ))}
        {period === 'custom' && (
          <>
            <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
              className="h-7 text-[11px] w-36" data-testid="invoices-custom-start" />
            <span className="text-gray-400 text-xs">até</span>
            <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
              className="h-7 text-[11px] w-36" data-testid="invoices-custom-end" />
          </>
        )}

        <div className="flex-1" />

        {canExport && invoices.length > 0 && (
          <Button onClick={handleExport} className="btn-primary text-[10px] h-8 px-3" data-testid="invoices-export-btn">
            <Download size={12} className="mr-1" /> EXPORTAR EXCEL
          </Button>
        )}
      </div>

      {/* CAPTURE BUTTONS */}
      <div className="card-hud p-4 border-2 border-dashed border-gray-300 hover:border-gold/50 transition-all">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { handleUpload(e.target.files); e.target.value = null; }}
          data-testid="invoices-file-input"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => { handleUpload(e.target.files); e.target.value = null; }}
          data-testid="invoices-camera-input"
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <p className="text-navy font-rajdhani font-semibold text-sm uppercase tracking-wider">
              CAPTURAR NOTA FISCAL
            </p>
            <p className="text-gray-400 text-[10px] font-mono mt-1">
              OCR automático extrai data, item e valor total. Você pode editar antes de exportar.
            </p>
            {uploadCount > 0 && (
              <p className="text-gold text-[10px] font-mono mt-1">
                Processando {uploadDone}/{uploadCount}...
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => cameraInputRef.current?.click()}
              className="btn-primary text-[10px] h-9 px-4"
              data-testid="invoices-capture-camera"
            >
              <Camera size={14} className="mr-1" /> CÂMERA
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="text-[10px] h-9 px-4 border-gold/40 text-gold hover:bg-gold/10"
              data-testid="invoices-capture-upload"
            >
              <Upload size={14} className="mr-1" /> UPLOAD
            </Button>
          </div>
        </div>
      </div>

      {/* CATEGORY TOTALS (overview cards) */}
      {invoices.length > 0 && totalsByCategory.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="invoices-category-summary">
          {totalsByCategory.map((tc) => (
            <div
              key={tc.code}
              className={`rounded-lg p-2 border ${CATEGORY_COLORS[tc.code] || CATEGORY_COLORS.OUTROS} border-current/20`}
              data-testid={`category-card-${tc.code}`}
            >
              <p className="text-[9px] font-bold uppercase tracking-wider truncate">{tc.label}</p>
              <p className="text-sm font-bold font-mono mt-0.5">{fmtCurrency(tc.total)}</p>
              <p className="text-[9px] font-mono opacity-70 mt-0.5">{tc.count} {tc.count === 1 ? 'nota' : 'notas'}</p>
            </div>
          ))}
        </div>
      )}

      {/* TABLE */}
      <div className="card-hud overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 size={20} className="text-gold animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm" data-testid="invoices-empty">
            <FileText size={28} className="mx-auto mb-2 text-gray-300" />
            Nenhuma nota fiscal encontrada neste período.
            <br />Capture sua primeira nota com a câmera ou faça upload.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="invoices-table">
              <thead className="bg-yellow-100/50">
                <tr className="border-b border-gray-200">
                  <th className="px-2 py-2 text-left text-[10px] font-bold text-navy uppercase tracking-wider w-16">FOTO</th>
                  <th className="px-2 py-2 text-left text-[10px] font-bold text-navy uppercase tracking-wider">DATA</th>
                  <th className="px-2 py-2 text-left text-[10px] font-bold text-navy uppercase tracking-wider">ITEM</th>
                  <th className="px-2 py-2 text-right text-[10px] font-bold text-navy uppercase tracking-wider">VALOR</th>
                  <th className="px-2 py-2 text-left text-[10px] font-bold text-navy uppercase tracking-wider">CATEGORIA</th>
                  <th className="px-2 py-2 text-left text-[10px] font-bold text-navy uppercase tracking-wider">STATUS</th>
                  <th className="px-2 py-2 text-right text-[10px] font-bold text-navy uppercase tracking-wider">AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <InvoiceRow
                    key={inv.id}
                    inv={inv}
                    categories={categories}
                    canEditAll={canExport}
                    token={token}
                    onChange={(updated) =>
                      setInvoices((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                    }
                    onDelete={(id) => setInvoices((prev) => prev.filter((x) => x.id !== id))}
                    onOpenPhoto={openPhotoFor}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-yellow-100/60 border-t-2 border-gold/40">
                  <td colSpan={3} className="px-2 py-2 text-right text-navy font-bold text-xs uppercase tracking-wider">
                    TOTAL GERAL
                  </td>
                  <td className="px-2 py-2 text-right text-navy font-bold text-sm font-mono" data-testid="invoices-total">
                    {fmtCurrency(total)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Photo lightbox */}
      {lightboxIndex >= 0 && lightboxItems[lightboxIndex] && (
        <PhotoLightbox
          photos={lightboxItems}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
        />
      )}
    </div>
  );
};

export default InvoicesPage;
